import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearBufferReport,
  hardReset,
  processData,
  processList,
  realtimeCountGet,
  realtimeListGet,
  realtimeLiveDataGet
} from '../../../store/slices/tenantSlices/realtimeReportSlice';
import { Button, Card, Col, Row } from 'reactstrap';
import FormSelect from '../../../components/form/FormSelect';
import { refreshTimeOptions } from '../../../utils/common';
import ProcessDetails from './ProcessDetails';
import ReportCount from './ReportCount';
import ReportList from './ReportList';
import ReportLiveData from './ReportLiveData';
import AdminHeader from '../../../components/header/AdminHeader';
import { toast } from 'react-toastify';
import ClearBufferConfirmationModal from './ClearBufferConfirmationModal';
import HardResetConfirmationModal from './HardResetConfirmationModal';
import { useUserDetails } from '../../../store/slices/authSlice';
import RecentCallModal from '../Configuration/RecentCallModal';
import { filterRowsByStatus, getStatusCard } from './realtimeStatus';

// Every /realtime endpoint has always taken a single `selected_process_id`. Multi-select
// sends a comma-separated list; a single selection still sends the bare id, so nothing
// regresses for one process if the backend has not shipped multi-id support yet. If the
// backend wants a different shape (repeated params, JSON array), this is the only place
// to change.
const toProcessParam = (ids) => (ids?.length ? ids.join(',') : undefined);

const RealtimeReport = () => {
  const user = useUserDetails();

  const dispatch = useDispatch();

  // Replaces the old processId + hyperProcessId pair. `hyperProcessId` only ever existed to
  // override the dropdown with one process; with an array, drilling in is just a selection
  // of one, which removes the scalar-vs-array split that ran through five call sites.
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [refreshTime, setRefreshTime] = useState(5);
  const [processOptions, setProcessOptions] = useState([]);
  const [proData, setProData] = useState([]);
  const [countData, setCountData] = useState([]);
  const [listData, setListData] = useState([]);
  const [liveData, setLiveData] = useState({ ringing_data: [], waiting_data: [] });
  const [refresh, setRefresh] = useState();
  const [bufferClearModalOpen, setBufferClearModalOpen] = useState(false);
  const [recentCallModalOpen, setRecentCallModalOpen] = useState(false);
  const [hardResetModalOpen, setHardResetModalOpen] = useState(false);

  const getProcessList = async () => {
    try {
      const response = await dispatch(processList()).unwrap();
      const options =
        response && response.data.length > 0
          ? response.data.map((item) => ({ label: item.name, value: item.id }))
          : [];
      setProcessOptions([...(options || [])]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const getProcessData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(processData(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        const sortedData = response.data.sort((a, b) => {
          return (
            (a.process_status_final === 'true' ? -1 : 1) -
            (b.process_status_final === 'true' ? -1 : 1)
          );
        });
        setProData(sortedData);
      } else {
        setProData([]);
      }
    } catch (error) {
      setProData([]);
      console.log('error', error);
    }
  };

  const getCountData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(realtimeCountGet(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        setCountData(response.data);
      } else {
        setCountData([]);
      }
    } catch (error) {
      setCountData([]);
      console.log('error', error);
    }
  };

  const getListData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(realtimeListGet(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        setListData(response.data);
      } else {
        setListData([]);
      }
    } catch (error) {
      setListData([]);
      console.log('error', error);
    }
  };

  const getLiveReportData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(realtimeLiveDataGet(payload)).unwrap();
      if (response && response?.success) {
        setLiveData({
          ringing_data: response.ringing_data?.length ? response.ringing_data : [],
          waiting_data: response.waiting_data?.length ? response.waiting_data : []
        });
      } else {
        setLiveData([]);
      }
    } catch (error) {
      setLiveData([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessList();
  }, []);

  useEffect(() => {
    const fetchData = () => {
      const id = toProcessParam(selectedProcessIds);
      getLiveReportData(id);
      getProcessData(id);
      getCountData(id);
      getListData(id);
    };

    fetchData();

    if (refreshTime) {
      const intervalId = setInterval(fetchData, refreshTime * 1000);

      return () => clearInterval(intervalId);
    }
  }, [refreshTime, selectedProcessIds, refresh]);

  // The status filter is held here, not inside ReportCount, so the poll above can replace
  // listData every few seconds without dropping the supervisor's selection.
  const visibleListData = useMemo(
    () => filterRowsByStatus(listData, selectedStatus),
    [listData, selectedStatus]
  );

  const listEmptyMessage = selectedStatus
    ? `No agents in ${getStatusCard(selectedStatus)?.title}`
    : 'No data available';

  const onBufferClear = async () => {
    try {
      const response = await dispatch(
        clearBufferReport({ selected_process_id: toProcessParam(selectedProcessIds) })
      ).unwrap();
      toast.success(response?.message);
      setBufferClearModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  const onHardReset = async () => {
    try {
      const response = await dispatch(hardReset()).unwrap();
      toast.success(response?.message);
      setHardResetModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <Fragment>
      <AdminHeader title="Realtime Report" />
      <Row>
        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 p-2">
            <Row>
              <Col lg="12" md="12">
                {/* No hand-rolled "All" option: FormSelect injects its own "Select All"
                    when isMulti, and an empty selection already means every process to
                    the API. Two select-everything entries in one menu is a bug. */}
                <FormSelect
                  isShowLabel={false}
                  isMulti
                  name="processId"
                  value={selectedProcessIds}
                  placeholder="All Processes"
                  options={processOptions}
                  onChange={(e) => setSelectedProcessIds(e.target.value || [])}
                />
              </Col>
              {user?.type !== 'supervisor' ? (
                <Col lg="12" md="12">
                  <Button
                    color="primary"
                    className="px-4 w-100"
                    type="submit"
                    onClick={() => setHardResetModalOpen(true)}>
                    Hard Reset
                  </Button>
                </Col>
              ) : null}
            </Row>
          </Card>
          <Card className="m-0">
            {/* Clicking a process drills into it: the selection narrows to that one. */}
            <ProcessDetails
              data={proData}
              setHyperProcessId={(id) => setSelectedProcessIds([id])}
              activeIds={selectedProcessIds}
            />
          </Card>
        </Col>
        <Col lg="8" md="8" className="px-2">
            <ReportCount
              data={countData}
              selectedStatus={selectedStatus}
              onSelectStatus={setSelectedStatus}
            />
          <Card className="m-0">
            <ReportList
              data={visibleListData}
              setRefresh={setRefresh}
              onSelectProcess={(id) => setSelectedProcessIds([id])}
              emptyMessage={listEmptyMessage}
            />
          </Card>
        </Col>
        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 p-2">
            <Row>
              <Col lg="12" md="12">
                <FormSelect
                  isShowLabel={false}
                  name="refreshTime"
                  value={refreshTime}
                  placeholder="Select Refresh"
                  options={refreshTimeOptions}
                  onChange={(e) => setRefreshTime(e.target.value)}
                />
              </Col>
              <Col lg="12" md="12" className="mb-2">
                <Button
                  color="primary"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setBufferClearModalOpen(true)}>
                  Clear Buffer
                </Button>
              </Col>
              <Col lg="12" md="12">
                <Button
                  color="violet"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setRecentCallModalOpen(true)}>
                  Recent Call
                </Button>
              </Col>
            </Row>
          </Card>
          <div className="live-data-container">
            <Card className="m-0">
              <ReportLiveData data={liveData?.ringing_data} title="Ringing" />
            </Card>
            <Card className="m-0">
              <ReportLiveData data={liveData?.waiting_data} title="Waiting" />
            </Card>
          </div>
        </Col>
      </Row>
      <ClearBufferConfirmationModal
        isOpen={bufferClearModalOpen}
        onClearClick={() => onBufferClear()}
        onCloseClick={() => setBufferClearModalOpen(false)}
      />
      <HardResetConfirmationModal
        isOpen={hardResetModalOpen}
        onResetClick={() => onHardReset()}
        onCloseClick={() => setHardResetModalOpen(false)}
      />
      <RecentCallModal
        isOpen={recentCallModalOpen}
        processOptions={processOptions}
        onCloseClick={() => setRecentCallModalOpen(false)}
      />
    </Fragment>
  );
};

export default RealtimeReport;
